import React from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";

const DashboardTile = ({
    title,
    icon,
    count,
    link,
    colorClass = "text-primary",
}: {
    title: string;
    icon?: string;
    count?: string | number;
    link: string;
    colorClass?: string;
}) => {
    return (
        <Link to={link} className="text-decoration-none col-xxl-2 col-xl-2 col-lg-3 col-md-4 col-sm-6 col-6 mb-3">
            <div
                className="card h-100 border-0 shadow-sm hover-card"
                style={{
                    borderRadius: "16px",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    overflow: "hidden",
                    backgroundColor: "#fff"
                }}
            >
                <div className="card-body d-flex flex-column align-items-center justify-content-center text-center p-3">

                    {/* Icon Section - Large Relative to Card */}
                    <div
                        className={`d-flex align-items-center justify-content-center mb-2 rounded-circle`}
                        style={{
                            width: "48px",
                            height: "48px",
                            backgroundColor: "rgba(var(--bs-primary-rgb), 0.05)",
                            border: "1px solid rgba(0,0,0,0.03)"
                        }}
                    >
                        {icon && <i className={`ti ${icon} ${colorClass}`} style={{ fontSize: "24px" }}></i>}
                    </div>

                    {/* Text Section - Small & Compact */}
                    <h6 className="fw-bold text-dark mb-0 w-100 px-1 text-truncate" title={title} style={{ fontSize: "12px", letterSpacing: "0.2px" }}>
                        {title}
                    </h6>

                    {/* Badge/Count Section */}
                    {count !== undefined && (
                        <div className="mt-2">
                            <span className={`badge rounded-pill bg-light ${colorClass} border`} style={{ fontSize: "10px", padding: "3px 8px", fontWeight: "600" }}>
                                {count} {count === 1 ? 'Item' : 'Items'}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
};

const DashboardSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
    return (
        <div className="mb-4">
            <h5 className="fw-bold text-secondary mb-3 ms-1" style={{ fontSize: "15px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {title}
            </h5>
            <div className="row">{children}</div>
        </div>
    );
};

const AdminOverview: React.FC = () => {
    const routes = all_routes as any;

    // Mock Data for Admin
    const stats = {
        pendingRequests: 12,
        vacantHouses: 5,
        occupiedHouses: 142,
        maintenancePending: 3,
    };

    const dashboardSections = [
        {
            title: "Overview",
            tiles: [
                { title: "Total Requests", icon: "ti-file-import", count: stats.pendingRequests, link: routes.staffQuarterAdmin.requests, colorClass: "text-warning" },
                { title: "Vacant Houses", icon: "ti-home-off", count: stats.vacantHouses, link: routes.staffQuarterAdmin.houses, colorClass: "text-success" },
                { title: "Occupied Flats", icon: "ti-home", count: stats.occupiedHouses, link: routes.staffQuarterAdmin.houses, colorClass: "text-primary" },
                { title: "Total Allotments", icon: "ti-home-check", count: 150, link: routes.staffQuarterAdmin.allotments, colorClass: "text-info" }
            ]
        },
        {
            title: "Management",
            tiles: [
                { title: "Priority List", icon: "ti-list-ol", count: 24, link: routes.staffQuarterAdmin.priorityList, colorClass: "text-success" },
                { title: "Approve Requests", icon: "ti-checkbox", count: stats.pendingRequests, link: routes.staffQuarterAdmin.requests, colorClass: "text-primary" },
                { title: "Maintenance", icon: "ti-tool", count: stats.maintenancePending, link: "#", colorClass: "text-danger" },
                { title: "Circulars", icon: "ti-bell", link: routes.circulars.list, colorClass: "text-secondary" }
            ]
        },
        {
            title: "Reports",
            tiles: [
                { title: "Vacation History", icon: "ti-history", link: routes.staffQuarterAdmin.reports, colorClass: "text-teal" }
                // Commented out tiles can be added here if needed
            ]
        }
    ];

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                {/* Page Header */}
                <div className="page-header mb-4">
                    <div className="row align-items-center">
                        <div className="col">
                            <h3 className="page-title text-dark fw-bold">Staff Quarters Admin</h3>
                            <ul className="breadcrumb bg-transparent p-0 mt-1">
                                <li className="breadcrumb-item">
                                    <Link to={routes.adminDashboard}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item active">Staff Quarters Admin</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Dashboard Content */}
                {dashboardSections.map((section, index) => (
                    <DashboardSection key={index} title={section.title}>
                        {section.tiles.map((tile, tIndex) => (
                            <DashboardTile
                                key={tIndex}
                                title={tile.title}
                                icon={tile.icon}
                                count={tile.count}
                                link={tile.link}
                                colorClass={tile.colorClass}
                            />
                        ))}
                    </DashboardSection>
                ))}

            </div>

            <style>{`
        .hover-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
        }
        .text-purple { color: #6f42c1 !important; }
        .text-orange { color: #fd7e14 !important; }
        .text-teal { color: #20c997 !important; }
      `}</style>
        </div>
    );
};

export default AdminOverview;
